import { replaceWhitespace } from "@utils/common.util";
import {
  WHITESPACES,
  STANDARD_RESIDUES,
} from "@services/common/config/common.config";
import {
  BinderDesignTypeEnum,
  VhhDesignTypeEnum,
} from "@services/home/enum/home.enum";
import { checkLigandCCDApi } from "@services/home/home.api";
import {
  ConstraintModel,
  SequenceRowModel,
  ProvideStructureDataModel,
} from "@services/home/model/home.model";
import {
  checkRna,
  checkDna,
  checkLigandSmiles,
  checkPdbId,
  checkAlphafoldId,
} from "@utils/data-process.util";
import { Page } from "@services/common/model/page.model";
import { AxiosError } from "axios";

export function asyncEmailValidate(val: string) {
  const reg =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
  if (!val) return "This field is required";

  if (!val.match(reg)) {
    return "Invalid emial!";
  }

  // const { code } = await checkEmailApi(val);
  // if (code !== 200) {
  //   return "Email address already in use. ";
  // }

  return "";
}

export function phoneValidate(val: string) {
  const reg = /^1[3456789]\d{9}$/;
  if (!val) return "This field is required";
  if (!val.match(reg)) {
    return "Invalid phone number";
  }

  return "";
}

export function emailValidate(val: string) {
  const regEmail =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
  if (!val) return "This field is required";
  if (!val.match(regEmail)) {
    return "Invalid email address";
  }

  return "";
}

export function passwordValidate(val: string, values: any) {
  const reg = /(?=.*[0-9])(?=.*[a-zA-Z])(?=.*[^a-zA-Z0-9]).{8,30}/;
  if (!val) {
    return "This field is required";
  } else if (val.length < 8) {
    return "At least 8 characters long";
  } else if (!val.match(reg)) {
    return "Must include letter, number and symbol";
  }
  return "";
}

export function confirmPasswordValidate(
  val: string,
  values: Record<string, any>
) {
  if (!val) {
    return "This field is required";
  } else if (val.length < 8) {
    return "At least 8 characters long";
  } else if (val !== values.password) {
    return "Don't match with password";
  }
  return "";
}

export function codeValidate(val: string, values: any) {
  const reg = /\d{6}/;
  if (!val) {
    return "This field is required";
  } else if (!val.match(reg)) {
    return "Invalid verification code.";
  }

  return "";
}

export function sizeRangeValidate(
  val: string,
  values: { startSize?: number; endSize?: number }
) {
  const startSize = values.startSize;
  const endSize = values.endSize;
  if (
    startSize !== undefined &&
    endSize !== undefined &&
    startSize >= endSize
  ) {
    return "Size order is invalid";
  }
  return "";
}

export function deleteAccountValidate(val: string) {
  if (val !== "delete my account")
    return `Please enter the exact phrase "delete my account" to confirm your request.`;

  return "";
}

function allIntersect(ranges: number[][]) {
  // Create separate arrays for the min and max values.
  const minimums = ranges.map((r) => r[0]);
  const maximums = ranges.map((r) => r[1]);
  // Extract the comparands.
  const maximalMinimum = Math.max(...minimums);
  const minimalMaximum = Math.min(...maximums);
  return maximalMinimum <= minimalMaximum;
}

export function epitopeValidate(val: string) {
  if (!val) {
    return "This field is required";
  }
  const reg = /^([a-z0-9A-Z]:\d+-\d+,)*([a-z0-9A-Z]:\d+-\d+)$/;
  val = val.trim();
  if (!reg.test(val)) {
    return "Epitope is invalid";
  }
  const arr = val.split(",");
  arr.sort();
  const map: Record<string, string[]> = {};
  arr.forEach((range) => {
    if (!map[range[0]]) {
      map[range[0]] = [range.slice(2)];
    } else {
      map[range[0]].push(range.slice(2));
    }
  });

  const hasIntersection = Object.keys(map).some((k) => {
    const ranges = map[k].map((range) => {
      const rangeNums = range.split("-");
      const [min, max] = rangeNums;
      return [+min, +max];
    });
    if (ranges.length > 1 && allIntersect(ranges)) {
      return true;
    } else {
      return false;
    }
  });

  if (hasIntersection) {
    return "Range has overlap";
  }
  return "";
}

export function heavyChainValidate(val: string) {
  const text = replaceWhitespace(val);
  if (!text) {
    return "This field is required";
  }

  const len = text.length;

  for (let i = 0; i < len; i++) {
    const c = text[i];

    const whiteList = `${WHITESPACES.join("")}`;

    if (whiteList.includes(c)) {
      continue;
    }

    const validResidues = `${STANDARD_RESIDUES}X`;

    if (!validResidues.includes(c)) {
      return `Found unsupported residue '${c}'`;
    }
  }

  // [H3,Number] appear once
  // const reg = /^([A-Z]*(\[H3,\d+\]){1}[A-Z]*)$/;

  // if (!reg.test(text)) {
  //   return 'There must be one and only "[H3,{len}]" in the chain';
  // }

  // delete [H3,Number]
  // const normalText = text.replace(/\[H3,\d+\]/g, "");

  // extract Number from [H3,Number]
  // const matchResult = text.match(/\[H3,(\d+)\]/);

  // let count = normalText.length;

  // if (matchResult && matchResult.length > 1) {
  //   count += Number(matchResult[1]);
  // }

  // if (text.length > 150) {
  //   return "Chain sequence too long";
  // }

  return "";
}

export function lightChainValidate(val: string) {
  if (!val) {
    return "This field is required";
  }

  const reg = /^([A-Z]*(\[L\d+,\d+\])*[A-Z]*)*$/;

  if (!reg.test(val)) {
    return "Chain is invalid";
  }

  return "";
}

export function antibodyChainValidate(val: string, isIncludeX = false) {
  const text = replaceWhitespace(val);
  if (!text) return "This field is required";
  const len = text.length;
  for (let i = 0; i < len; i++) {
    const c = text[i];

    if (isIncludeX) {
      if (![...STANDARD_RESIDUES, "X"].includes(c)) {
        return `Found unsupported residue '${c}'`;
      }
    } else {
      if (!STANDARD_RESIDUES.includes(c)) {
        return `Found unsupported residue '${c}'`;
      }
    }
  }
  if (text.length > 1000) {
    return "Sequence too long (max 1000).";
  }
  return "";
}

export function chainNameValidate(
  val: string,
  record: Record<string, any>,
  i: number,
  key: string,
  chainNameOptions: string[]
) {
  const map = record as { constraints: ConstraintModel[] };
  const item = map.constraints[i];
  const text = replaceWhitespace(val);
  if (!text) return "This field is required";

  if (!chainNameOptions.includes(text)) {
    return "No such chain";
  }

  if (key === "chain_id_b") {
    if (text === item.chain_id_a) {
      return "Chain is same";
    }
  } else if (key === "chain_id_a") {
    if (text === item.chain_id_b) {
      return "Chain is same";
    }
  }

  return "";
}

interface RangeModel {
  type: "single" | "range";
  start?: number;
  end?: number;
  value?: number;
}

function parseNumberRangeString(input: string) {
  const result: RangeModel[] = [];
  const parts = input.split(",");

  for (const part of parts) {
    if (part.includes("-")) {
      const [start, end] = part.split("-").map(Number);
      result.push({ type: "range", start, end });
    } else {
      result.push({ type: "single", value: Number(part) });
    }
  }

  return result;
}

export function sequenceLengthValidate(
  val: string,
  map: Record<string, SequenceRowModel>,
  chainName: string
) {
  const pattern = /^(\d+(-\d+)?)(,(\d+(-\d+)?))*$/;
  const text = replaceWhitespace(val);
  if (!text) return "This field is required";

  if (!pattern.test(text)) {
    return "Invalid format";
  }

  if (!chainName) {
    return "";
  }

  const seqItem = map[chainName];

  if (!seqItem.sequence) {
    return "";
  }

  const len = seqItem.sequence.length;

  const arr = parseNumberRangeString(text);

  const valid = arr.every((item) => {
    if (item.type === "single") {
      return item.value! <= len && item.value! > 0;
    } else {
      return (
        item.start! <= len &&
        item.start! > 0 &&
        item.end! <= len &&
        item.end! > 0 &&
        item.start! < item.end!
      );
    }
  });

  if (!valid) {
    return "Invalid range";
  }

  return "";
}

const blackList = [
  "abc",
  "xyz",
  "test",
  "none",
  "hello",
  "world",
  "example",
  "demo",
  "null",
  "empty",
  "undefined",
  "n/a",
  "abc123",
  "localhost",
  "testing",
  "org",
  "organization",
  "company",
  "sample",
];

export function organizationValidate(val: string) {
  const text = replaceWhitespace(val);
  const numberRegx = /^\d+$/;

  if (!text) {
    return "This field is required";
  }

  if (text.length < 3) {
    return "Invalid organization name";
  }

  if (numberRegx.test(text)) {
    return "Invalid organization name";
  }

  const input = text.toLowerCase();

  if (blackList.includes(input)) {
    return "Invalid organization name";
  }

  if (/^([a-zA-Z0-9])\1*$/.test(input)) {
    return "Invalid organization name";
  }

  return "";
}

export function isStructureSegmentValid(val: string) {
  const regex = /^([A-Z]):(\d+(?:-\d+)?)(?:,([A-Z]):(\d+(?:-\d+)?))*$/;

  const v = replaceWhitespace(val);
  if (!v) return "Nothing Inputed";

  if (!regex.test(v)) {
    return "Invalid format";
  }

  return "";
}

export async function binderSequenceValidate(val: string, type: string) {
  const inputText = replaceWhitespace(val);

  try {
    if (type === BinderDesignTypeEnum.Ligand) {
      const msg = await checkLigandSmiles(inputText);
      return msg;
    } else if (type === BinderDesignTypeEnum.LigandCCD) {
      const { code, message } = await checkLigandCCDApi([inputText]);

      if (code !== 200) {
        return message;
      }
    } else if (type === BinderDesignTypeEnum.RnaSequence) {
      const msg = checkRna(inputText);
      if (msg) {
        return msg;
      }
    } else if (type === BinderDesignTypeEnum.DnaSequenceDs) {
      const msg = checkDna(inputText);
      if (msg) {
        return msg;
      }
    } else if (type === BinderDesignTypeEnum.ProteinChain) {
      const msg = antibodyChainValidate(inputText, true);
      if (msg) {
        return msg;
      }
    }
  } catch (e) {
    if (e instanceof AxiosError) {
      const error = e as AxiosError<Page<null>>;
      const { response } = error;
      if (response) {
        return response.data.message;
      }
    }
  }

  return "";
}

export async function ccdCodeValidate(val: string) {
  const regex = /^(\([^)]+\)|[^()]+)+$/;

  const text = replaceWhitespace(val);
  if (!text) return "This field is required";

  if (!regex.test(text)) {
    return "Invalid format";
  }

  const normalResidues = text.replace(/\([^)]*\)/g, "");
  const len = normalResidues.length;
  for (let i = 0; i < len; i++) {
    const c = normalResidues[i];
    if (!STANDARD_RESIDUES.includes(c)) {
      return `Found unsupported residue '${c}'`;
    }
  }

  const ccdCodeList = [...text.matchAll(/\(([^)]+)\)/g)].map((m) => m[1]);
  if (ccdCodeList.length > 0) {
    try {
      const { code, message } = await checkLigandCCDApi(ccdCodeList);

      if (code !== 200) {
        return message;
      }
    } catch (e) {
      const error = e as AxiosError;
      const data = error.response?.data as Page<null>;
      if (data.code !== 200) {
        return data.message;
      }
    }
  }

  return "";
}

export function vhhDesignTypeValidate(val: string, type?: string) {
  const id = replaceWhitespace(val);
  if (!id) return "This field is required";

  if (type === VhhDesignTypeEnum.PdbID) {
    const msg = checkPdbId(id);
    return msg || "";
  } else if (type === VhhDesignTypeEnum.UniProtID) {
    const msg = checkAlphafoldId(id);
    return msg || "";
  } else if (type === VhhDesignTypeEnum.AntigenSequence) {
    const msg = antibodyChainValidate(id);
    return msg || "";
  }

  return "";
}

export function parseRanges(input: string) {
  return input.split(",").map((part) => {
    if (part.includes("-")) {
      const [start, end] = part.split("-").map(Number);
      return [start, end] as [number, number];
    } else {
      const num = Number(part);
      return [num, num] as [number, number];
    }
  });
}

export function structSegmentValidate(val: string, record: unknown) {
  // const regex = /([A-Z]):(\d+)([A-Z]?)(?:-(\d+)([A-Z]?))?/;
  const regex = /^([A-Z]):(\d+)(?:-(\d+))?$/;

  const v = replaceWhitespace(val);
  if (!v) return "This field is required";

  const values = record as ProvideStructureDataModel;

  if (!values.input_value) {
    return "";
  }

  if (!regex.test(v)) {
    return "Invalid format";
  }

  return "";
}

export function seqSegmentValidate(val: string, entitySequence?: string) {
  const v = replaceWhitespace(val);
  if (!v) return "This field is required";

  if (!entitySequence) {
    return "No sequence exist";
  }

  const regex = /^(\d+(-\d+)?)(,(\d+(-\d+)?))*$/;

  if (!regex.test(v)) {
    return "Invalid format";
  }

  if (entitySequence) {
    const len = entitySequence.length;

    const matrix = parseRanges(v);

    const invalid = matrix.some(([start, end]) => {
      if (start > end) {
        return true;
      } else if (start < 1 || start > len) {
        return true;
      } else if (end < 1 || end > len) {
        return true;
      } else {
        return false;
      }
    });

    if (invalid) {
      return "Out of range";
    }
  }

  return "";
}
